import React, { useEffect } from "react";
import styles from "./machines-list-page.modules.scss";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { ContainerPanel } from "@src/components/organisms/container-panel/container-panel";
import { ContainerPane } from "@src/components/organisms/container-pane/container-pane";
import { MachinesListCompaniesWrapper } from "@src/components/organisms/machines-list-companies-wrapper/machines-list-companies-wrapper";
import { useMachinesListPageHook } from "./use-machines-list-page-hook";
import { useI18N } from "@src/language/language-context";
import Spinner from "@src/components/atoms/spinner/spinner";
import { MachinesListSearchBar } from "@src/components/organisms/machines-list-search/machines-list-searchbar";
import { useLocation, useNavigate } from "react-router-dom";

const cssClassName = "machines-list-page";

export const MachinesListPage = () => {
  const i18n = useI18N();
  const location = useLocation();
  const navigate = useNavigate();
  const targetMachineSourceId = new URLSearchParams(location.search).get(
    "source_id",
  );

  const {
    loading,
    allMachinesListData,
    showSearchMachinesOrAllMachinesListData,
    showNoSearchResults,
    machinesManagerSearchTerms,
    machinesManagerSearchQuery,
    machinesManagerShowSearchBar,
    handleSearchResults,
    handleSearchTermsChange,
    handleSearchQueryChange,
  } = useMachinesListPageHook();

  useEffect(() => {
    if (!targetMachineSourceId) return;

    const clearHighlightTimeout = setTimeout(() => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("source_id");
      const nextSearch = searchParams.toString();
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    }, 30000);

    return () => clearTimeout(clearHighlightTimeout);
  }, [targetMachineSourceId, navigate]);

  useEffect(() => {
    if (!targetMachineSourceId || loading) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;

    const scrollToMachine = () => {
      if (cancelled) return;

      const targetMachineRow = document.getElementById(
        `machine-row-${targetMachineSourceId}`,
      );
      if (targetMachineRow) {
        targetMachineRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }

      attempts += 1;
      if (!cancelled && attempts < maxAttempts) {
        window.setTimeout(scrollToMachine, 100);
      }
    };

    scrollToMachine();

    return () => {
      cancelled = true;
    };
  }, [targetMachineSourceId, loading]);

  const texts = {
    title: i18n("modules.machinesListPage.title"),
    noSearchResults: i18n("modules.search.noResults"),
  };

  return (
    <MainContainer>
      <RoundBorderComponentContainer>
        <ContainerPanel>
          <SubHeader>
            <SubTitle text={texts.title} />
          </SubHeader>
          <ContainerPane>
            {loading ? (
              <div className={styles[`${cssClassName}__loading`]}>
                <Spinner />
              </div>
            ) : (
              <>
                {machinesManagerShowSearchBar && (
                  <MachinesListSearchBar
                    allMachinesListData={allMachinesListData}
                    machinesManagerSearchTerms={machinesManagerSearchTerms}
                    machinesManagerSearchQuery={machinesManagerSearchQuery}
                    onSearchTermsChange={handleSearchTermsChange}
                    onSearchQueryChange={handleSearchQueryChange}
                    onSearchResults={handleSearchResults}
                  />
                )}
                {showNoSearchResults ? (
                  <div className={styles[`${cssClassName}__empty-wrapper`]}>
                    <p className={styles[`${cssClassName}__empty-message`]}>
                      {texts.noSearchResults}
                    </p>
                  </div>
                ) : (
                  <MachinesListCompaniesWrapper
                    showSearchMachinesOrAllMachinesListData={
                      showSearchMachinesOrAllMachinesListData
                    }
                    selectedSourceId={targetMachineSourceId || undefined}
                  />
                )}
              </>
            )}
          </ContainerPane>
        </ContainerPanel>
      </RoundBorderComponentContainer>
    </MainContainer>
  );
};
